import { type GetQuoteResponse } from '@qdrant/qdrant-cloud-public-api/booking/v1';
import { AnalyticsBrowser, type MiddlewareFunction } from '@segment/analytics-next';
import { millicentsToMonthlyPrice } from '@/components/Billing/helpers';
import { objectEntries, objectFromEntries } from '@/helpers/functional';
import { HUBSPOT_UTK_TLD_COOKIE, HUBSPOT_UTK_COOKIE, HUBSPOT_SIGNUP_URL_NAME } from '@/utils/analytics-utils';
import { Cookies } from '@/utils/cookies';
import { safeSessionStorage } from '@/utils/helpers';
import { retrieveInitialReferrer } from '@/utils/referrer';
import { retrieveUtmParams } from '@/utils/utm';
import { type AuthenticationType, type InteractionEvent, type LoginAndSignupEvent } from './events.types';

export const analytics = new AnalyticsBrowser();

/**
 * Current campaign values attribution.
 * Via middleware it is part of every 'page', 'track', 'identify', 'group', and 'alias' call payload.
 */
export function campaignAttributionMiddleware({ payload, next }: Parameters<MiddlewareFunction>[0]) {
  if (payload.obj.properties) {
    payload.obj.properties = {
      ...payload.obj.properties,
      initialReferrer: retrieveInitialReferrer(),
    };
  }

  if (!payload.obj.context) {
    next(payload);
    return;
  }

  const params = retrieveUtmParams();
  if (!params) {
    next(payload);
    return;
  }
  payload.obj.context.campaign = params;

  next(payload);
}

/**
 * Track page views every time a path changes
 * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
 */
export function trackPageView(pathname: string) {
  return analytics.page(document.title, {
    url: pathname,
    ...getGAProperties(),
    [`qdrant_tech_${HUBSPOT_UTK_COOKIE}`]: Cookies.get(HUBSPOT_UTK_TLD_COOKIE),
  });
}

let gaSessionId: string | undefined;
let gaClientId: string | undefined;

// Google Analytics Cookies
export function getGAProperties() {
  if (!(gaSessionId && gaClientId)) {
    const gaMeasurementId = window.__QDRANT_CLOUD__.ga_measurement_id.replace('G-', '');
    gaSessionId = Cookies.get(`_ga_${gaMeasurementId}`)?.replace('GS1.1.', '').split('.')[0];
    gaClientId = Cookies.get('_ga')?.replace('GA1.1.', '');
  }
  return { ga_client_id: gaClientId, ga_session_id: gaSessionId };
}

export function identifyUser({ userId, email, timestamp }: { userId: string; email?: string; timestamp?: Date }) {
  return analytics.identify(
    userId,
    {
      email,
      [HUBSPOT_UTK_COOKIE]: Cookies.get(HUBSPOT_UTK_COOKIE),
      [`qdrant_tech_${HUBSPOT_UTK_COOKIE}`]: Cookies.get(HUBSPOT_UTK_TLD_COOKIE),
    },
    { timestamp, version: window.__QDRANT_CLOUD__.version },
  );
}

let didInitialUtmAttribution = false;

export function initialUtmAttribution() {
  if (didInitialUtmAttribution) {
    return;
  }
  didInitialUtmAttribution = true;

  const params = retrieveUtmParams({ firstTouch: true });
  if (!params) {
    return;
  }

  const traits = objectFromEntries(objectEntries(params).map(([k, v]) => [`initial_${k}` as const, v]));

  void analytics.identify(traits);
}

let didJustAuthenticate = false;

export function setDidJustAuthenticate() {
  didJustAuthenticate = true;
}

export function trackLoginAndSignup({
  email,
  loginCount,
  authProviderName,
}: {
  email: string;
  loginCount: number;
  authProviderName: AuthenticationType;
}) {
  if (!didJustAuthenticate) {
    return;
  }
  didJustAuthenticate = false;

  const eventName: 'login' | 'signup' = loginCount < 2 ? 'signup' : 'login';
  const eventPayload: LoginAndSignupEvent = {
    authenticationType: authProviderName,
  };

  const isSignup = eventName === 'signup';
  const signupUrl = isSignup ? safeSessionStorage.get(HUBSPOT_SIGNUP_URL_NAME) : null;

  if (isSignup) {
    window.dataLayer ??= [];
    (window.dataLayer as Record<string, string | null>[]).push({
      email,
      event: eventName,
      signupUrl,
    });
  }

  // Hubspot
  window._hsq = window._hsq || [];
  window._hsq.push(['identify', { email }]);

  void analytics.track(eventName, eventPayload, {
    traits: {
      email,
      signupUrl,
      [HUBSPOT_UTK_COOKIE]: Cookies.get(HUBSPOT_UTK_COOKIE),
    },
  });
}

export function trackInteraction(payload: InteractionEvent) {
  return analytics.track('interaction', { ...payload, ...getGAProperties() });
}

export function trackLoginLinked({ email, authProviderName }: { email: string; authProviderName: AuthenticationType }) {
  return analytics.track('login_linked', { authProviderName, email });
}

/**
 * GTM event for a new paid cluster or a free→paid upgrade.
 * `price.value` is the discounted monthly price the summary shows, rounded to cents.
 * Without a quote `price` is null.
 */
export function trackPaidClusterCreated(quote?: Pick<GetQuoteResponse, 'currency' | 'discountedPricePerHour'>) {
  window.dataLayer ??= [];

  if (!quote) {
    window.dataLayer.push({ event: 'paid-cluster-created', price: null });
    return;
  }

  const value = Math.round(millicentsToMonthlyPrice(quote.discountedPricePerHour) * 100) / 100;
  window.dataLayer.push({ event: 'paid-cluster-created', price: { currency: quote.currency, value } });
}
